import React, { useState, useEffect } from "react";
import { TextField, IconButton, InputAdornment } from "@mui/material";
import { baseURL } from '../backend_url';
import Swal from "sweetalert2";
import axios from 'axios';
import Iconify from '../login/Iconify';
import {
  buildMyProfilePayload,
  myProfileErrorMessage,
  postMyProfile,
  requireMyProfileSession,
  requirePasswordMatch,
  requireProfileSave,
  requireSecurityAnswers,
} from './myProfileRequest';

const question1 = "In what city were you born?";
const question2 = "What is the name of your favorite pet?";
const question3 = "At what age did you start school?";

function showMessage(icon, title) {
  return Swal.fire({
    position: 'center',
    icon,
    title,
    showConfirmButton: false,
    timer: 3000,
  });
}

export default function UpdateForm() {

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [npassword, setNPassword] = useState("");
  const [showNPassword, setShowNPassword] = useState(false);
  const [cnpassword, setCNPassword] = useState("");
  const [showCNPassword, setShowCNPassword] = useState(false);
  const [loggedInUserDetail, setLoggedInUserDetail] = useState(null);
  const [sq1, setSq1] = useState("");
  const [sq2, setSq2] = useState("");
  const [sq3, setSq3] = useState("");
  const [changePassword, setChangePassword] = useState(false);
  const [isBusy, setIsBusy] = useState(true);

  useEffect(() => {
    let active = true;

    function loadSession() {
      try {
        const foundUser = requireMyProfileSession(sessionStorage);
        if (!active) return;

        setLoggedInUserDetail(foundUser);
      } catch (error) {
        if (active) {
          showMessage(
            'error',
            myProfileErrorMessage(error, 'Profile details could not be loaded.')
          );
        }
      } finally {
        if (active) setIsBusy(false);
      }
    }

    loadSession();
    return () => {
      active = false;
    };
  }, []);

  const profilePayload = (values = {}) => (
    buildMyProfilePayload(loggedInUserDetail, values)
  );

  const handleVerify = async (e) => {
    e.preventDefault();

    if (password.trim() === '') {
      showMessage('error', 'Please enter your current password');
      return;
    }
    if (sq1.trim() === '') {
      showMessage('error', 'Please fill answer for security question 1');
      return;
    }
    if (sq2.trim() === '') {
      showMessage('error', 'Please fill answer for security question 2');
      return;
    }
    if (sq3.trim() === '') {
      showMessage('error', 'Please fill answer for security question 3');
      return;
    }

    const confirmation = await Swal.fire({
      title: 'Do you really want to save changes?',
      showCancelButton: true,
      confirmButtonText: 'Yes',
    });
    if (!confirmation.isConfirmed) return;

    const newPassword = npassword.trim();
    const confirmedPassword = cnpassword.trim();
    const passwordChangeRequested = newPassword !== '' || confirmedPassword !== '';

    if (passwordChangeRequested && newPassword === '') {
      showMessage('error', 'New Password field can\'t be empty');
      return;
    }
    if (passwordChangeRequested && confirmedPassword === '') {
      showMessage('error', 'Confirm New Password field can\'t be empty');
      return;
    }
    if (passwordChangeRequested && newPassword === password.trim()) {
      showMessage('error', 'New Password must be different from Current Password');
      return;
    }
    if (passwordChangeRequested && newPassword !== confirmedPassword) {
      showMessage('error', 'New Password didn\'t match with Confirm New Password');
      return;
    }
    if (passwordChangeRequested && newPassword.length < 8) {
      showMessage('error', 'Your new password must be at least 8 characters');
      return;
    }
    if (passwordChangeRequested && newPassword.search(/[a-z]/i) < 0) {
      showMessage('error', 'Your new password must contain at least one letter');
      return;
    }
    if (passwordChangeRequested && newPassword.search(/[0-9]/) < 0) {
      showMessage('error', 'Your new password must contain at least one digit');
      return;
    }

    setIsBusy(true);
    try {
      const matched = await postMyProfile(
        axios,
        baseURL,
        '/check_password_my_profile',
        profilePayload({ password: password.trim() }),
        requirePasswordMatch
      );
      if (!matched) {
        showMessage('error', 'Current password didn\'t match');
        return;
      }

      const answers = {
        s_a1: sq1.trim(),
        s_a2: sq2.trim(),
        s_a3: sq3.trim(),
      };
      const endpoint = passwordChangeRequested
        ? '/save_my_profile_data_1'
        : '/save_my_profile_data_2';
      const saveValues = passwordChangeRequested
        ? {
          ...answers,
          current_password: password.trim(),
          password: newPassword,
        }
        : { ...answers, current_password: password.trim() };

      await postMyProfile(
        axios,
        baseURL,
        endpoint,
        profilePayload(saveValues),
        requireProfileSave
      );

      showMessage('success', 'Updated Successfully');
      setChangePassword(false);
      setPassword("");
      setNPassword("");
      setCNPassword("");
      setSq1("");
      setSq2("");
      setSq3("");
    } catch (error) {
      showMessage(
        'error',
        myProfileErrorMessage(error, 'Profile could not be updated.')
      );
    } finally {
      setIsBusy(false);
    }
  };

  const handlePasswordConfirm1 = async (e) => {
    e.preventDefault();

    if (password.trim() === '') {
      showMessage('error', 'Please enter your current password');
      return;
    }

    setIsBusy(true);
    try {
      const matched = await postMyProfile(
        axios,
        baseURL,
        '/check_password_my_profile',
        profilePayload({ password: password.trim() }),
        requirePasswordMatch
      );

      if (!matched) {
        showMessage('error', 'Current password didn\'t match');
        return;
      }

      const answers = await postMyProfile(
        axios,
        baseURL,
        '/get_user_sqs',
        profilePayload({ password: password.trim() }),
        requireSecurityAnswers
      );

      setSq1(answers.s_a1);
      setSq2(answers.s_a2);
      setSq3(answers.s_a3);
      setChangePassword(true);
      setNPassword("");
      setCNPassword("");
    } catch (error) {
      showMessage(
        'error',
        myProfileErrorMessage(error, 'Password could not be verified.')
      );
    } finally {
      setIsBusy(false);
    }
  };


  return (
    <>
      <div className="row pt-4">
        <div className="col-8 mx-3">
          <span style={{ color: '#1E1E1E', fontSize: '21px' }}>Update your profile details: </span>
        </div>
      </div>

      <div className="row pt-5">
        {/* <div className="col-1"></div> */}
        <div className="col-8 mx-3">
          <form className="row g-3" aria-busy={isBusy}>

            <div className="row">

              <div className="col-md-5 mb-3">

                <TextField
                  fullWidth
                  autoComplete="current-password"
                  type={showPassword ? 'text' : 'password'}
                  label="Current Password"
                  value={password}
                  disabled={isBusy}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

              </div>

            </div>

            {
              !changePassword && <>
                <div className="row mt-1">
                  <div className="col-12 mb-4 ">

                    {/* <a onClick={() => { setChangePassword(!changePassword); handlePasswordConfirm1() }} style={{ color: 'red' }}> */}
                    {/* {!changePassword ? "Do you want to change your current password, Click here" : "If you do not want to change your current password, Click here"} */}
                    {/* {!changePassword ? "Enter your current password and click here to proceed ahead" : ""} */}
                    {/* </a> */}

                    <div className="col-md-5"></div>
                    <div className="col-md-2">

                      <button type="submit" className="btn btn-success" disabled={isBusy} onClick={(e) => { handlePasswordConfirm1(e); }}>
                        {isBusy ? 'Verifying...' : 'Verify'}
                      </button>
                    </div>

                  </div>


                </div>
              </>}

            {
              changePassword && <>

                <div className="row">

                  <div className="col-md-5 mb-3">

                    <TextField
                      fullWidth
                      autoComplete="new-password"
                      type={showNPassword ? 'text' : 'password'}
                      label="New Password"
                      value={npassword}
                      disabled={isBusy}
                      onChange={(e) => setNPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowNPassword(!showNPassword)} edge="end">
                              <Iconify icon={showNPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                  </div>

                </div>

                <div className="row">

                  <div className="col-md-5 mb-3">

                    <TextField
                      fullWidth
                      autoComplete="confirm-password"
                      type={showCNPassword ? 'text' : 'password'}
                      label="Confirm New Password"
                      value={cnpassword}
                      disabled={isBusy}
                      onChange={(e) => setCNPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowCNPassword(!showCNPassword)} edge="end">
                              <Iconify icon={showCNPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                  </div>

                </div>




                {/* Security Question Start */}
                <div className='row mt-4'>
                  <div className="col-md-2 mb-3">
                    <label htmlFor="company_name" className="form-label">
                      Security Question 1
                    </label>
                  </div>
                  <div className="col-md-3  mb-3">


                    <label htmlFor="q1" className="form-label">
                      {question1}
                    </label>

                  </div>
                  <div className="col-md-3  mb-3">

                    <input
                      type="text"
                      className="form-control"
                      value={sq1}
                      id="q1"
                      disabled={isBusy}
                      onChange={(e) => setSq1(e.target.value)}
                    />
                  </div>
                  {/* <div className="col-md-1 mb-3"></div> */}
                  <div className="col-md-2 mb-3">

                  </div>
                  <div className="col-md-2 mb-3">
                    {/* <input
                  type="text"
                  className="form-control"
                  value={company_id}
                  id="company_id"
                  disabled
                /> */}
                  </div>
                </div>

                <div className='row'>
                  <div className="col-md-2 mb-3">
                    <label htmlFor="company_name" className="form-label">
                      Security Question 2
                    </label>
                  </div>
                  <div className="col-md-3  mb-3">


                    <label htmlFor="q2" className="form-label">
                      {question2}
                    </label>

                  </div>
                  <div className="col-md-3  mb-3">
                    <input
                      type="text"
                      className="form-control"
                      value={sq2}
                      id="q2"
                      disabled={isBusy}
                      onChange={(e) => setSq2(e.target.value)}
                    />
                  </div>
                  {/* <div className="col-md-1 mb-3"></div> */}
                  <div className="col-md-2 mb-3">

                  </div>
                  <div className="col-md-2 mb-3">
                    {/* <input
                  type="text"
                  className="form-control"
                  value={company_id}
                  id="company_id"
                  disabled
                /> */}
                  </div>

                </div>

                <div className='row'>
                  <div className="col-md-2 mb-3">
                    <label htmlFor="company_name" className="form-label">
                      Security Question 3
                    </label>
                  </div>
                  <div className="col-md-3  mb-3">


                    <label htmlFor="q3" className="form-label">
                      {question3}
                    </label>

                  </div>
                  <div className="col-md-3  mb-3">

                    <input
                      type="text"
                      className="form-control"
                      value={sq3}
                      id="q3"
                      disabled={isBusy}
                      onChange={(e) => setSq3(e.target.value)}
                    />
                  </div>
                  {/* <div className="col-md-1 mb-3"></div> */}
                  <div className="col-md-2 mb-3">

                  </div>
                  <div className="col-md-2 mb-3">
                    {/* <input
                  type="text"
                  className="form-control"
                  value={company_id}
                  id="company_id"
                  disabled
                /> */}
                  </div>

                </div>
                {/* Security Question End */}

                <div className="col-md-2"></div>
                <div className="row">
                  <div className="col-md-5 mb-4 ">
                    <button type="submit" className="btn btn-success" disabled={isBusy} onClick={handleVerify}>
                      {isBusy ? 'Updating...' : 'Update'}
                    </button>
                  </div>

                </div>
              </>}


          </form>

        </div>

      </div>
    </>
  );
}
